import { isDesktop } from '@/platform';
import { blobToBase64 } from '@/data/mirror/mirror-payload';
import type { NoteImage } from '@/data/db';
import type { MirrorChange } from '@/data/mirror/mirror-bridge';

export interface ClipboardImageSource {
  filePath?: string | null;
  base64?: string | null;
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (!isDesktop()) throw new Error('圖片檔案功能請使用 Windows 本機版');
  const core = await import('@tauri-apps/api/core');
  try {
    return await core.invoke<T>(command, args);
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

export async function storeNoteImageFile(
  noteId: string,
  id: string,
  source: File | ClipboardImageSource,
): Promise<Omit<NoteImage, 'createdAt'>> {
  const payload = source instanceof File ? { base64: await blobToBase64(source) } : source;
  return invoke('note_image_store', { noteId, id, ...payload });
}

export async function readNoteImageFile(noteId: string, id: string): Promise<Uint8Array> {
  return new Uint8Array(await invoke<ArrayBuffer>('note_image_read', { noteId, id }));
}

export async function restoreNoteImageFile(meta: NoteImage, bytes: Uint8Array): Promise<void> {
  await invoke('note_image_restore', {
    meta,
    base64: await blobToBase64(new Blob([bytes as BlobPart])),
  });
}

export async function removeNoteImageFile(noteId: string, id: string): Promise<void> {
  await invoke('note_image_remove', { noteId, id });
}

/** Places the image pixels on the Windows clipboard for pasting into another application. */
export async function copyNoteImageToClipboard(noteId: string, id: string): Promise<void> {
  await invoke('note_image_copy', { noteId, id });
}

/** Save a byte-for-byte copy of the stored PNG to a user-selected path. */
export async function exportNoteImageFile(
  noteId: string,
  id: string,
  destination: string,
): Promise<void> {
  await invoke('note_image_export', { noteId, id, destination });
}

/** Open Explorer with the immutable local PNG selected. */
export async function revealNoteImageFile(noteId: string, id: string): Promise<void> {
  await invoke('note_image_reveal', { noteId, id });
}

export async function pickNoteImageDestination(defaultName: string): Promise<string | null> {
  if (!isDesktop()) return null;
  const dialog = await import('@tauri-apps/plugin-dialog');
  const chosen = await dialog.save({
    defaultPath: defaultName,
    filters: [{ name: 'PNG 圖片', extensions: ['png'] }],
  });
  return chosen ?? null;
}

export async function clearNoteImageFiles(): Promise<void> {
  await invoke('note_images_clear');
}

export function hasNoteImageOperations(noteId: string): boolean {
  return (pending.get(noteId)?.size ?? 0) > 0;
}

export async function readClipboardImages(): Promise<ClipboardImageSource[]> {
  return invoke('note_clipboard_images');
}

export async function buildBackupDatabase(
  changes: MirrorChange[],
  at: string,
): Promise<Uint8Array> {
  return new Uint8Array(await invoke<ArrayBuffer>('backup_database', { changes, at }));
}

export async function writeBackupZip(
  bytes: Uint8Array,
  destination: string | null = null,
): Promise<string> {
  return invoke('backup_write', {
    destination,
    base64: await blobToBase64(new Blob([bytes as BlobPart])),
  });
}

export async function readBackupZip(path: string): Promise<File> {
  const bytes = await invoke<ArrayBuffer>('backup_read', { path });
  return new File([bytes], path.split(/[\\/]/).at(-1) ?? 'backup.zip', { type: 'application/zip' });
}

const pending = new Map<string, Set<Promise<unknown>>>();

/** Covers clipboard reads, file writes, insertion and its immediate IndexedDB save. */
export function trackNoteImageOperation<T>(noteId: string, operation: Promise<T>): Promise<T> {
  const operations = pending.get(noteId) ?? new Set();
  pending.set(noteId, operations);
  operations.add(operation);
  void operation
    .finally(() => {
      operations.delete(operation);
      if (operations.size === 0) pending.delete(noteId);
    })
    .catch(() => {});
  return operation;
}

export async function waitForNoteImageOperations(noteId?: string): Promise<void> {
  while (true) {
    const operations = noteId
      ? [...(pending.get(noteId) ?? [])]
      : [...pending.values()].flatMap((set) => [...set]);
    if (operations.length === 0) return;
    await Promise.allSettled(operations);
  }
}
